import {
  createContext,
  Dispatch,
  KeyboardEventHandler,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
} from 'react';

export const DropdownContext = createContext<{
  activeDropdown: string | null;
  setActiveDropdown: Dispatch<SetStateAction<string | null>>;
}>({
  activeDropdown: null,
  setActiveDropdown: () => {},
});

let timeout: NodeJS.Timeout | null = null;

const useDropdown = (id: string) => {
  const { activeDropdown, setActiveDropdown } = useContext(DropdownContext);

  const handleOnMouseDown = useCallback<MouseEventHandler>(
    e => {
      e.preventDefault();
      if (activeDropdown === id) {
        setActiveDropdown(null);
      } else {
        setActiveDropdown(id);
      }
    },
    [activeDropdown, setActiveDropdown, id],
  );

  const handleOnKeyDown = useCallback<KeyboardEventHandler<any>>(
    e => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (activeDropdown === id) {
          setActiveDropdown(null);
        } else {
          setActiveDropdown(id);
        }
      }
    },
    [activeDropdown, setActiveDropdown, id],
  );

  const handleOnMouseLeave = useCallback(() => {
    timeout = setTimeout(() => {
      if (activeDropdown === id) {
        setActiveDropdown(null);
      }
    }, 500);
  }, [setActiveDropdown, activeDropdown, id]);

  const handleOnMouseEnter = useCallback(() => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    setActiveDropdown(id);
  }, [setActiveDropdown, id]);

  const handleToggle = useCallback<MouseEventHandler>(
    e => {
      e.preventDefault();
      if (activeDropdown === id) {
        setActiveDropdown(null);
      } else {
        setActiveDropdown(id);
      }
    },
    [activeDropdown, id, setActiveDropdown],
  );

  const handleClose = useCallback(() => {
    setActiveDropdown(null);
  }, [setActiveDropdown]);

  return {
    open: activeDropdown === id,
    handlers: {
      handleClose,
      handleOnKeyDown,
      handleOnMouseDown,
      handleOnMouseEnter,
      handleOnMouseLeave,
      handleToggle,
    },
  };
};

export default useDropdown;
